import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CompanyMatcherV2Service } from '../../services/company-matcher-v2.service';
import { GlobalSearchService } from '../../services/global-search.service';
import { baseUrl } from '../../globals';
import { ZintNgxSliderComponent } from '../zint-ngx-slider/zint-ngx-slider.component';
import { TaggingComponent } from '../tagging/tagging.component';

@Component({
    selector: 'company-matcher',
    templateUrl: './company-matcher.component.html',
    styleUrls: ['./company-matcher.component.css'],
    standalone: false
})
export class CompanyMatcherComponent implements OnInit {
  lowerMatchLevel: number = null;
  upperMatchLevel: number = null;
  matcherTagPayload:
    | {
        context: 'selectedOnPage';
        rowIdsToSelectedCompanyId: Record<number, number>;
      }
    | {
        context: 'all';
        lowerMatchLevel: number;
        upperMatchLevel: number;
      }
    | null = null;

  @Input()
  inputParams: any = {
    searchMatcherDetailsEndpoint: '',
    downloadMatcherDetailsForFilterEndpoint: '',
    downloadMatcherDetailsForPageEndpoint: '',
  };

  @ViewChild('matchLevelSliderElement')
  matchLevelSliderElement: ZintNgxSliderComponent;

  @ViewChild('matcherTagging')
  matcherTaggingComponent: TaggingComponent;

  total: any;
  numberOfResults: any;
  pageNumber = 1;
  resultsPerPage = 100;
  numberOfPages: any;

  isLoading = this.companyMatcherV2Service.isLoading;
  columnHeaders: Array<any>;
  rowsOfMatchedData: Array<any>;

  companyNameSearch = '';
  shortlistedCompaniesByName: Array<any>;

  canCreateRecords = false;

  defaultMatcherHeaderSet = new Set(
    ['Company Number', 'Company Name', 'Address', 'Website', 'Phone Number'].map(
      header => header.toLowerCase()
    )
  );

  rowIdShowingSearch;
  private readonly CRM_MATCHER_ROUTE = '/matcher/crm';
  private readonly DOWNLOAD_REGULAR_MATCHER_FOR_PAGE_ENDPOINT =
    '/download_regular_matcher_records_for_page/';
  private readonly DOWNLOAD_REGULAR_MATCHER_FOR_FILTER_ENDPOINT =
    '/download_regular_matcher_records_with_filter/';

  constructor(
    private companyMatcherV2Service: CompanyMatcherV2Service,
    private companyMatcherGlobalSearchService: GlobalSearchService
  ) {}

  ngOnInit(): void {
    this.lowerMatchLevel = 0;
    this.upperMatchLevel = 100;

    window.addEventListener('beforeunload', function (e) {
      e.preventDefault();
      e.returnValue = '';
    });
  }

  loadDataForUser(): void {
    this.searchMatcherResults(true);
  }

  updateRadio(possibleMatch, possibleMatchesInRow): void {
    const radioInput = document.getElementById(
      'possibleMatch__' + possibleMatch.id
    ) as HTMLInputElement;
    const originalSelectState = possibleMatch.selected;
    this.unselectPossibleMatches(possibleMatchesInRow);
    if (!originalSelectState) {
      radioInput.checked = true;
      possibleMatch.selected = possibleMatch.id;
    }
  }

  updateDataOnPage(data): void {
    this.columnHeaders = data.column_headers;
    this.rowsOfMatchedData = data.rows;
    this.numberOfResults = data.count;
    if (data.total) {
      this.total = data.total;
    }
    this.numberOfPages = Math.ceil(this.numberOfResults / this.resultsPerPage);
    this.updateMatchLevel(this.matchLevelSliderElement);
    this.updateCreateRecordsAvailability();
  }

  private getCurrentPathname(): string {
    if (typeof window === 'undefined' || !window.location) {
      return '';
    }
    return window.location.pathname || '';
  }

  private getMatcherTypeFromRoute(): 'Regular' | 'CRM' {
    const pathname = this.getCurrentPathname();
    if (
      pathname === this.CRM_MATCHER_ROUTE ||
      pathname.startsWith(`${this.CRM_MATCHER_ROUTE}/`)
    ) {
      return 'CRM';
    }
    return 'Regular';
  }

  private shouldAttachMatcherType(endpoint: string): boolean {
    const normalizedEndpoint = endpoint?.trim();
    return (
      normalizedEndpoint === this.DOWNLOAD_REGULAR_MATCHER_FOR_PAGE_ENDPOINT ||
      normalizedEndpoint === this.DOWNLOAD_REGULAR_MATCHER_FOR_FILTER_ENDPOINT
    );
  }

  private matcherTypeQueryParam(endpoint: string): string {
    if (!this.shouldAttachMatcherType(endpoint)) {
      return '';
    }
    return '&matcherType=' + this.getMatcherTypeFromRoute();
  }

  downloadPageOfResults(): void {
    var rowIds = this.rowsOfMatchedData.map(function (row) {
      return row.row_id;
    });
    const endpoint = this.inputParams.downloadMatcherDetailsForPageEndpoint;
    const matcherTypeParam = this.matcherTypeQueryParam(endpoint);
    window.open(
      baseUrl +
        endpoint +
        '?rowIds=[' +
        rowIds +
        ']' +
        matcherTypeParam
    );
  }

  downloadAllResults(): void {
    const endpoint = this.inputParams.downloadMatcherDetailsForFilterEndpoint;
    const matcherTypeParam = this.matcherTypeQueryParam(endpoint);
    window.open(
      baseUrl +
        endpoint +
        '?lowerMatchLevel=' +
        this.lowerMatchLevel +
        '&upperMatchLevel=' +
        this.upperMatchLevel +
        matcherTypeParam
    );
  }

  unselectPossibleMatches(possibleMatches): void {
    possibleMatches.forEach(function (possibleMatch) {
      const radioInput = document.getElementById(
        'possibleMatch__' + possibleMatch.id
      ) as HTMLInputElement;
      radioInput.checked = false;
      possibleMatch.selected = null;
    });
  }

  selectAllFirstMatches(): void {
    this.rowsOfMatchedData.forEach(function (row) {
      if (row.possible_matches && row.possible_matches.length > 0) {
        const radioInput = document.getElementById(
          'possibleMatch__' + row.possible_matches[0].id
        ) as HTMLInputElement;
        radioInput.checked = true;
        row.possible_matches[0].selected = row.possible_matches[0].id;
      }
    });
  }

  processCompaniesFromNameSearch(data): void {
    if (data) {
      this.shortlistedCompaniesByName = data;
    }
  }

  matcherSearchForCompanyByName(): void {
    if (this.companyNameSearch.length > 2) {
      this.companyMatcherGlobalSearchService
        .searchCompanyByName(this.companyNameSearch)
        .subscribe(data => this.processCompaniesFromNameSearch(data));
    }
  }

  addCompanyToRow(row, company): void {
    row.possible_matches.push({
      id: 'uniqueId' + String(Date.now()),
      number: company.company_number,
      name: company.company_name,
      company_id: company.company_id,
      probability: null,
    });
  }

  updateMatchLevel(sliderEvent: any): void {
    if (sliderEvent['lower'] !== undefined) {
      this.lowerMatchLevel = sliderEvent['lower'];
    }
    if (sliderEvent['upper'] !== undefined) {
      this.upperMatchLevel = sliderEvent['upper'];
    }
  }

  searchMatcherResults(isInit): void {
    this.companyMatcherV2Service
      .searchMatchedData(
        this.inputParams.searchMatcherDetailsEndpoint,
        this.lowerMatchLevel,
        this.upperMatchLevel,
        this.pageNumber,
        this.resultsPerPage,
        isInit
      )
      .subscribe(data => this.updateDataOnPage(data));
  }

  @Output() ParentFunctionForSelectedOnPage: EventEmitter<any> =
    new EventEmitter();
  ChildFunctionForSelectedOnPage(): void {
    var rowIdsToSelectedCompanyIds = {};
    for (var row of this.rowsOfMatchedData) {
      for (var possibleMatch of row.possible_matches) {
        if (possibleMatch.selected) {
          rowIdsToSelectedCompanyIds[parseInt(row.row_id)] =
            possibleMatch.company_id;
        }
      }
    }
    this.ParentFunctionForSelectedOnPage.emit(rowIdsToSelectedCompanyIds);
  }

  @Output() ParentFunctionForAll: EventEmitter<any> = new EventEmitter();
  ChildFunctionForAll(): void {
    this.ParentFunctionForAll.emit([
      this.lowerMatchLevel,
      this.upperMatchLevel,
    ]);
  }

  @Output() ParentCreateMatcherTagsForSelectedOnPage: EventEmitter<any> =
    new EventEmitter();
  @Output() ParentCreateMatcherTagsForAll: EventEmitter<any> =
    new EventEmitter();

  openTagModalForSelectedOnPage(): void {
    var rowIdsToSelectedCompanyIds = {};
    for (var row of this.rowsOfMatchedData) {
      for (var possibleMatch of row.possible_matches) {
        if (possibleMatch.selected) {
          rowIdsToSelectedCompanyIds[parseInt(row.row_id)] =
            possibleMatch.company_id;
        }
      }
    }
    this.matcherTagPayload = {
      context: 'selectedOnPage',
      rowIdsToSelectedCompanyId: rowIdsToSelectedCompanyIds,
    };
    if (this.matcherTaggingComponent) {
      const selectedCount = Object.keys(rowIdsToSelectedCompanyIds).length;
      this.matcherTaggingComponent.openMatcherModal({
        type: 'selectedOnPage',
        description:
          selectedCount > 0
            ? `Apply a tag to ${selectedCount} selected match${
                selectedCount === 1 ? '' : 'es'
              } on this page.`
            : 'No matches are currently selected on this page.',
      });
    }
  }

  openTagModalForAll(): void {
    this.matcherTagPayload = {
      context: 'all',
      lowerMatchLevel: this.lowerMatchLevel,
      upperMatchLevel: this.upperMatchLevel,
    };
    if (this.matcherTaggingComponent) {
      const description = `Apply a tag to the top results for all ${this.numberOfResults} rows between ${this.lowerMatchLevel}% and ${this.upperMatchLevel}% match confidence.`;
      this.matcherTaggingComponent.openMatcherModal({
        type: 'all',
        description,
      });
    }
  }

  handleMatcherTagSelection(event: {
    tagId: number;
    context: 'selectedOnPage' | 'all';
  }): void {
    if (!this.matcherTagPayload) {
      return;
    }
    if (
      event.context === 'selectedOnPage' &&
      this.matcherTagPayload.context === 'selectedOnPage'
    ) {
      const payload = this.matcherTagPayload;
      this.ParentCreateMatcherTagsForSelectedOnPage.emit({
        tagId: event.tagId,
        rowIdsToSelectedCompanyId: payload.rowIdsToSelectedCompanyId,
      });
    } else if (
      event.context === 'all' &&
      this.matcherTagPayload.context === 'all'
    ) {
      const payload = this.matcherTagPayload;
      this.ParentCreateMatcherTagsForAll.emit({
        tagId: event.tagId,
        lowerMatchLevel: payload.lowerMatchLevel,
        upperMatchLevel: payload.upperMatchLevel,
      });
    }
    this.matcherTagPayload = null;
  }

  @Output() ParentCancelMatchingProcess: EventEmitter<any> = new EventEmitter();
  ChildCancelMatchingProcess(): void {
    this.ParentCancelMatchingProcess.emit();
    setTimeout(() => this.loadDataForUser(), 1000);
  }

  private updateCreateRecordsAvailability(): void {
    this.canCreateRecords = false;

    for (const header of this.columnHeaders) {
      const name = (header?.CSVHeader ?? '').toString().trim().toLowerCase();
      if (name && !this.defaultMatcherHeaderSet.has(name)) {
        this.canCreateRecords = true;
        break;
      }
    }
  }
}
